// A person's cost rates on a given day, with the full build-up (PRD §3.2–3.3, EC4), how they change
// over time (the steps the daily engine costs plans with, EC6), and role standard rates for
// placeholders (EC7). Everything is derived from effective-dated records on every call, so a change
// re-costs from its date with nothing to recompute or migrate.

import { bonusOn, type BonusOn, type BonusType } from "./bonus";
import { annualizedCostItems, productiveHours } from "./cost";
import { addDays, addMonths, daysBetween, daysInYear, firstDayOf, isImplausibleDate, monthOf, weekdaysInYear, yearOf, type Iso } from "./dates";
import type { RateStep } from "./engine";
import { standardHolidays, weekdayHolidaysIn } from "./holidays";
import {
  COST_CATEGORY_LABEL,
  DEFAULT_CAPACITY,
  DEFAULT_PTO_DAYS,
  type Entered,
  inEffect,
  isActiveOn,
  isEmployee,
  live,
  type PayType,
  type Person,
} from "./people";
import { employerTaxes, type EmployerTaxes, type PayrollTaxYear } from "./tax";

export interface RateContext {
  taxTables: readonly PayrollTaxYear[];
  /** Company holiday dates, by the year of the list they were entered in. */
  holidayYears: ReadonlyMap<number, readonly Iso[]>;
  /** Bonus types, for names and whether each counts in OTE (PRD §3.4). */
  bonusTypes: readonly BonusType[];
  /** Everyone: role standard rates average the role's people. */
  people: readonly Person[];
}

export interface RateComponent {
  key: string;
  label: string;
  /** "base" lines make up loaded Base; "bonus" lines are added on top for loaded OTE. */
  group: "base" | "bonus";
  annual: number;
  perHour: number;
}

export interface RateCard {
  personId: string;
  day: Iso;
  /** null when no pay is in effect on the day. */
  type: PayType | null;
  basis: { capacityPerWeek: number; weekdays: number; holidays: number; ptoDays: number; state: string | null; taxYear: number | null };
  productiveHours: number;
  components: RateComponent[];
  /** The annual bonus the OTE rate carries on the day, after overrides. */
  targetBonus: number;
  /** How that bonus is made up: the letter's targets, and any overrides. */
  bonus: BonusOn;
  loadedBase: number;
  loadedOte: number;
  baseRate: number;
  oteRate: number;
  warnings: string[];
}

const ZERO_TAXES: EmployerTaxes = { socialSecurity: 0, medicare: 0, futa: 0, suta: 0, total: 0 };

/** The year's table, or the nearest other year's with a warning: a three-year plan must still cost. */
function taxTableFor(year: number, ctx: RateContext, warnings: string[]): PayrollTaxYear | null {
  const exact = ctx.taxTables.find((t) => t.year === year);
  if (exact) return exact;
  const sorted = [...ctx.taxTables].sort((a, b) => a.year - b.year);
  const nearest = sorted.filter((t) => t.year < year).pop() ?? sorted[0];
  if (!nearest) {
    warnings.push("There is no payroll tax table, so employer taxes are left out.");
    return null;
  }
  warnings.push(`There is no ${year} payroll tax table; using ${nearest.year}'s.`);
  return nearest;
}

function holidaysFor(year: number, ctx: RateContext, warnings: string[]): number {
  const dates = [...ctx.holidayYears.values()].flat();
  if (!ctx.holidayYears.has(year)) {
    warnings.push(`There is no ${year} holiday list; using the standard ten US holidays.`);
    dates.push(...standardHolidays(year).map((h) => h.date));
  }
  return weekdayHolidaysIn(year, dates);
}

/** Employer taxes, leaving SUTA out (with a warning) rather than failing when a state is missing. */
function taxesOn(wages: number, table: PayrollTaxYear | null, state: string | null, warnings: string[]): EmployerTaxes {
  if (!table) return ZERO_TAXES;
  let effective = table;
  if (!state || !table.suta[state]) {
    const message = !state
      ? "No work state is on file, so state unemployment tax (SUTA) is left out."
      : `The ${table.year} tax table has no SUTA rate for ${state}, so SUTA is left out.`;
    if (!warnings.includes(message)) warnings.push(message);
    effective = { ...table, suta: { ...table.suta, [state ?? "--"]: { rate: 0, wageBase: 0 } } };
  }
  return employerTaxes(wages, effective, state ?? "--");
}

/**
 * Records whose dates fall outside the years the app carries, nearly always a year typed as "26" that
 * a date picker stored as 0026. They still apply, so the card says which to withdraw and re-enter.
 */
export function implausibleDateWarnings(person: Person): string[] {
  const kinds: [string, readonly (Entered & { from: Iso; to?: Iso })[] | undefined][] = [
    ["pay", person.compRecords],
    ["capacity", person.capacity],
    ["work state", person.locations],
    ["cost", person.costItems],
    ["bonus letter", person.bonusTargets],
    ["bonus override", person.bonusOverrides],
  ];
  const out: string[] = [];
  for (const [kind, records] of kinds) {
    for (const r of live(records ?? [])) {
      const bad = [r.from, r.to].find(isImplausibleDate);
      if (bad) out.push(`A ${kind} record is dated ${bad}, which looks like a mistyped year. Withdraw it and enter it again with the right date.`);
    }
  }
  if (isImplausibleDate(person.endDate)) out.push(`The end date ${person.endDate} looks like a mistyped year. Correct it on the profile.`);
  return out;
}

export function rateCard(person: Person, day: Iso, ctx: RateContext): RateCard {
  const warnings: string[] = implausibleDateWarnings(person);
  const year = yearOf(day);
  const comp = inEffect(person.compRecords, day);
  const capacity = inEffect(person.capacity, day);
  const capacityPerWeek = capacity?.hoursPerWeek ?? DEFAULT_CAPACITY;
  const ptoDays = capacity?.ptoDays ?? DEFAULT_PTO_DAYS;
  if (!capacity) warnings.push(`No capacity is on file; using ${DEFAULT_CAPACITY} h/wk and ${DEFAULT_PTO_DAYS} PTO days.`);
  const weekdays = weekdaysInYear(year);
  const holidays = holidaysFor(year, ctx, warnings);
  const hours = productiveHours(capacityPerWeek, weekdays, holidays, ptoDays);
  const state = inEffect(person.locations, day)?.state ?? null;
  const basis = { capacityPerWeek, weekdays, holidays, ptoDays, state, taxYear: null as number | null };

  const empty = (message: string): RateCard => ({
    personId: person.id,
    day,
    type: comp?.type ?? null,
    basis,
    productiveHours: hours,
    components: [],
    targetBonus: 0,
    bonus: { lines: [], targetTotal: 0, totalOverride: null, total: 0 },
    loadedBase: 0,
    loadedOte: 0,
    baseRate: 0,
    oteRate: 0,
    warnings: [...warnings, message],
  });
  if (!comp) return empty(`No pay is in effect on ${day}.`);
  if (hours <= 0) return empty("Capacity, holidays and PTO leave no productive hours, so no rate can be computed.");

  const perHour = (annual: number) => annual / hours;
  const line = (key: string, label: string, annual: number, group: RateComponent["group"] = "base"): RateComponent => ({
    key,
    label,
    group,
    annual,
    perHour: perHour(annual),
  });

  const wages =
    comp.type === "exempt" || comp.type === "partner" ? comp.amount : comp.type === "contractor-monthly" ? comp.amount * 12 : comp.amount * hours;
  const payLabel = {
    exempt: "Base pay",
    hourly: "Wages (hourly rate × productive hours)",
    "contractor-hourly": "Fees (hourly rate × productive hours)",
    "contractor-monthly": "Fees (monthly fee × 12)",
    partner: "Guaranteed payments (K-1: no employer taxes)",
  }[comp.type];

  // Targets are annual amounts from the letter, so they spread over the year's productive hours as is.
  const bonus = bonusOn(person.bonusTargets, person.bonusOverrides, ctx.bonusTypes, day);
  const targetBonus = bonus.total;

  let taxesBase = ZERO_TAXES;
  let taxesOte = ZERO_TAXES;
  if (isEmployee(comp.type)) {
    const table = taxTableFor(year, ctx, warnings);
    basis.taxYear = table?.year ?? null;
    taxesBase = taxesOn(wages, table, state, warnings);
    taxesOte = taxesOn(wages + targetBonus, table, state, warnings);
  }

  const components: RateComponent[] = [line("pay", payLabel, wages)];
  if (isEmployee(comp.type)) {
    components.push(
      line("tax-ss", "Social Security (employer)", taxesBase.socialSecurity),
      line("tax-medicare", "Medicare (employer)", taxesBase.medicare),
      line("tax-futa", "Federal unemployment (FUTA)", taxesBase.futa),
      line("tax-suta", state ? `State unemployment (SUTA, ${state})` : "State unemployment (SUTA)", taxesBase.suta),
    );
  }
  for (const item of live(person.costItems)) {
    const annual = annualizedCostItems([item], day);
    if (annual > 0) components.push(line(`cost-${item.id}`, `${item.label} (${COST_CATEGORY_LABEL[item.category]})`, annual));
  }
  const costItems = annualizedCostItems(live(person.costItems), day);
  if (bonus.totalOverride !== null) {
    components.push(line("bonus-total", "Bonus (whole-bonus override)", bonus.totalOverride, "bonus"));
  } else {
    for (const l of bonus.lines) {
      if (l.includedInOte && l.amount > 0) components.push(line(`bonus-${l.typeId}`, `${l.override !== null ? "Bonus override" : "Target bonus"}: ${l.name}`, l.amount, "bonus"));
    }
  }
  const bonusTax = taxesOte.total - taxesBase.total;
  if (bonusTax > 0) components.push(line("tax-bonus", "Employer taxes on the bonus", bonusTax, "bonus"));

  const loadedBase = wages + taxesBase.total + costItems;
  const loadedOte = loadedBase + targetBonus + bonusTax;
  return {
    personId: person.id,
    day,
    type: comp.type,
    basis,
    productiveHours: hours,
    components,
    targetBonus,
    bonus,
    loadedBase,
    loadedOte,
    baseRate: perHour(loadedBase),
    oteRate: perHour(loadedOte),
    warnings,
  };
}

/**
 * The days on which a person's rate can change between `from` and `to`: any record's start, a cost
 * item's end or amortization end, a bonus letter or override's edges, and every 1 January (new tax table,
 * holidays and weekday count).
 */
export function rateChangeDays(person: Person, ctx: RateContext, from: Iso, to: Iso): Iso[] {
  const days = new Set<Iso>([from]);
  const add = (d: Iso | undefined) => d && d > from && d <= to && days.add(d);
  for (const r of live(person.compRecords)) add(r.from);
  for (const r of live(person.capacity)) add(r.from);
  for (const r of live(person.locations)) add(r.from);
  for (const c of live(person.costItems)) {
    add(c.from);
    if (c.to) add(addDays(c.to, 1));
    if (c.frequency === "one-time") add(firstDayOf(addMonths(monthOf(c.from), c.amortizeMonths ?? 12)));
  }
  for (const r of live(person.bonusTargets ?? [])) add(r.from);
  for (const r of live(person.bonusOverrides ?? [])) {
    add(r.from);
    if (r.to) add(addDays(r.to, 1));
  }
  for (let y = yearOf(from) + 1; y <= yearOf(to); y++) add(`${y}-01-01`);
  return [...days].sort();
}

/** Base and OTE rate steps from `from` to `to`, for the daily engine (§3.1) and the card's history. */
export function rateSteps(person: Person, ctx: RateContext, from: Iso, to: Iso): { base: RateStep[]; ote: RateStep[] } {
  const base: RateStep[] = [];
  const ote: RateStep[] = [];
  for (const day of rateChangeDays(person, ctx, from, to)) {
    const card = rateCard(person, day, ctx);
    if (base.at(-1)?.rate !== card.baseRate) base.push({ from: day, rate: card.baseRate });
    if (ote.at(-1)?.rate !== card.oteRate) ote.push({ from: day, rate: card.oteRate });
  }
  return { base, ote };
}

// --- Role standard rates (EC7) -------------------------------------------------------------------

export interface Role {
  id: string;
  name: string;
  active: boolean;
}

/** PRD §2.2: the seven seeded roles. Admins edit the list. */
export const SEEDED_ROLES: readonly Role[] = [
  { id: "executive-advisor", name: "Executive advisor", active: true },
  { id: "alliance-director", name: "Alliance director", active: true },
  { id: "alliance-manager", name: "Alliance manager", active: true },
  { id: "program-coordinator", name: "Program coordinator", active: true },
  { id: "consultant", name: "Consultant", active: true },
  { id: "partner-marketing", name: "Partner marketing", active: true },
  { id: "events-coordinator", name: "Events coordinator", active: true },
];

export interface RoleRateOverride extends Entered {
  roleId: string;
  from: Iso;
  baseRate: number;
  oteRate: number;
  note?: string;
}

export interface RoleRate {
  roleId: string;
  /** People in the role, working on the day, with pay on file: the ones the computed rate averages. */
  people: number;
  computed: { baseRate: number; oteRate: number } | null;
  override: RoleRateOverride | null;
  baseRate: number | null;
  oteRate: number | null;
  source: "override" | "computed" | "none";
  warnings: string[];
}

/**
 * A role's standard rate over time, for costing placeholders (PE3) and, for callers without pay
 * visibility, every row in that role (SEC3). It can change when an override starts, when any of the
 * role's people has a change, or on 1 January.
 */
export function roleRateSteps(
  role: Role,
  overrides: readonly RoleRateOverride[],
  ctx: RateContext,
  from: Iso,
  to: Iso,
): { base: RateStep[]; ote: RateStep[]; unrated: boolean } {
  const days = new Set<Iso>([from]);
  for (const o of overrides) if (o.roleId === role.id && !o.voided && o.from > from && o.from <= to) days.add(o.from);
  for (const p of ctx.people) {
    if (p.roleId !== role.id) continue;
    for (const d of rateChangeDays(p, ctx, from, to)) days.add(d);
    if (p.endDate && p.endDate >= from && p.endDate < to) days.add(addDays(p.endDate, 1));
  }
  const base: RateStep[] = [];
  const ote: RateStep[] = [];
  let unrated = false;
  for (const day of [...days].sort()) {
    const [r] = roleRates([role], overrides, ctx, day);
    if (r.baseRate === null) unrated = true;
    if (base.at(-1)?.rate !== (r.baseRate ?? 0)) base.push({ from: day, rate: r.baseRate ?? 0 });
    if (ote.at(-1)?.rate !== (r.oteRate ?? 0)) ote.push({ from: day, rate: r.oteRate ?? 0 });
  }
  return { base, ote, unrated };
}

/**
 * The computed rate is the hours-weighted mean of the role's people: their combined loaded cost ÷
 * their combined productive hours. An override in effect on the day replaces it.
 */
export function roleRates(roles: readonly Role[], overrides: readonly RoleRateOverride[], ctx: RateContext, day: Iso): RoleRate[] {
  return roles.map((role) => {
    let hours = 0;
    let baseCost = 0;
    let oteCost = 0;
    let people = 0;
    for (const person of ctx.people) {
      if (person.roleId !== role.id || !isActiveOn(person, day)) continue;
      const card = rateCard(person, day, ctx);
      if (!card.type || card.productiveHours <= 0) continue;
      people++;
      hours += card.productiveHours;
      baseCost += card.loadedBase;
      oteCost += card.loadedOte;
    }
    const computed = hours > 0 ? { baseRate: baseCost / hours, oteRate: oteCost / hours } : null;
    const override = inEffect(overrides.filter((o) => o.roleId === role.id), day) ?? null;
    const warnings: string[] = [];
    if (!override && people === 1) {
      warnings.push(
        "This rate comes from one person, so it is that person's rate. Anyone who sees costs without pay visibility could work it out. Set an override.",
      );
    }
    if (!override && !computed) warnings.push("Nobody in this role has pay on file. Placeholders in this role cost $0 until you set an override.");
    const chosen = override ?? computed;
    return {
      roleId: role.id,
      people,
      computed,
      override,
      baseRate: chosen?.baseRate ?? null,
      oteRate: chosen?.oteRate ?? null,
      source: override ? "override" : computed ? "computed" : "none",
      warnings,
    };
  });
}

// --- Target rates (§3.12, rev. 1.16) -----------------------------------------------------------------

/** What a role should cost: a target salary (or hourly rate, or monthly fee) and type, from a date. */
export interface RoleTarget extends Entered {
  roleId: string;
  from: Iso;
  type: PayType;
  /** Annual salary (exempt), hourly rate (hourly, 1099 hourly) or monthly fee (1099 monthly). */
  amount: number;
  /**
   * The annual bonus at target, for the Target OTE rate (rev. 1.16, Chris 2026-10-15). Entered as an
   * amount or worked out from an OTE split; absent or 0 means Target OTE equals Target Base.
   */
  bonus?: number;
  note?: string;
}

/** The bonus that makes base `basePct`% of OTE, for an annual base: 80% of OTE on $100,000 is $25,000. */
export const bonusFromSplit = (annualBase: number, basePct: number): number => (basePct > 0 && basePct <= 100 ? annualBase * (100 / basePct - 1) : 0);

/** A target's pay as an annual amount: salary, hourly rate × 2,080, or monthly fee × 12. */
export const annualTargetPay = (t: Pick<RoleTarget, "type" | "amount">): number =>
  t.type === "exempt" || t.type === "partner" ? t.amount : t.type === "contractor-monthly" ? t.amount * 12 : t.amount * 2_080;

/** One set of assumptions for burdening every target, since a target hire has no state or benefits yet. */
export interface TargetAssumptions {
  state: string;
  /** Employer benefits per month for an FTE (exempt or hourly); none for 1099s. */
  benefitsPerMonth: number;
  hoursPerWeek: number;
  ptoDays: number;
}

export const DEFAULT_TARGET_ASSUMPTIONS: TargetAssumptions = { state: "TX", benefitsPerMonth: 0, hoursPerWeek: DEFAULT_CAPACITY, ptoDays: DEFAULT_PTO_DAYS };

/** A role's targets as a stand-in person, so a target is burdened exactly as a person is (§3.2–3.3). */
export function targetPerson(roleId: string, targets: readonly RoleTarget[], a: TargetAssumptions): Person | null {
  // Oldest first; two targets from the same day: the one entered later wins, as for a person's records.
  const own = live(targets)
    .filter((t) => t.roleId === roleId)
    .sort((x, y) => x.from.localeCompare(y.from) || x.enteredAt.localeCompare(y.enteredAt));
  if (!own.length) return null;
  const stamp = { enteredAt: "1970-01-01T00:00:00.000Z", enteredBy: "Target assumptions" };
  const as = (t: RoleTarget) => ({ enteredAt: t.enteredAt, enteredBy: t.enteredBy });
  const first = own[0].from;
  return {
    id: `target:${roleId}`,
    name: "Target",
    roleId,
    timeZone: "America/Chicago",
    payIds: [],
    status: "active",
    compRecords: own.map((t) => ({ id: t.id, from: t.from, type: t.type, amount: t.amount, ...as(t) })),
    // The OTE bonus as a whole-bonus amount from each target's date; a target without one sets it to 0.
    bonusOverrides: own.map((t) => ({ id: `bonus-${t.id}`, from: t.from, typeId: null, amount: t.bonus ?? 0, ...as(t) })),
    // Benefits apply to employees only: a 1099 target simply carries none, because isEmployee gates taxes,
    // and the item is dropped while a 1099 type is in effect.
    costItems: a.benefitsPerMonth > 0
      ? own
          .map((t, i, all) => ({ t, next: all[i + 1]?.from }))
          .filter(({ t }) => isEmployee(t.type))
          .map(({ t, next }) => ({
            id: `benefits-${t.id}`,
            category: "healthcare" as const,
            label: "Benefits (target assumption)",
            amount: a.benefitsPerMonth,
            frequency: "monthly" as const,
            from: t.from,
            ...(next ? { to: addDays(next, -1) } : {}),
            ...as(t),
          }))
      : [],
    capacity: [{ id: "target-capacity", from: first, hoursPerWeek: a.hoursPerWeek, ptoDays: a.ptoDays, ...stamp }],
    locations: [{ id: "target-state", from: first, state: a.state, ...stamp }],
    bonusTargets: [],
  };
}

/** A role's target Base and OTE rate steps from `from` to `to`; empty when the role has no target. */
export function roleTargetSteps(roleId: string, targets: readonly RoleTarget[], a: TargetAssumptions, ctx: RateContext, from: Iso, to: Iso): { base: RateStep[]; ote: RateStep[] } {
  const person = targetPerson(roleId, targets, a);
  if (!person) return { base: [], ote: [] };
  return rateSteps(person, ctx, from, to);
}

/** The target rate card for a role on a day, with its build-up; null when the role has no target. */
export function targetCard(roleId: string, targets: readonly RoleTarget[], a: TargetAssumptions, ctx: RateContext, day: Iso): RateCard | null {
  const person = targetPerson(roleId, targets, a);
  return person ? rateCard(person, day, ctx) : null;
}
